import * as z from 'zod';

export const GenerateCheckoutSessionRequest = z.object({
  cart: z
    .object({
      items: z
        .array(
          z.object({
            name: z
              .string()
              .describe('Name of the item, shown on the checkout page.')
              .min(1)
              .max(255),
            quantity: z
              .number()
              .int()
              .describe('Quantity of this item to charge for.')
              .min(1),
            price: z
              .string()
              .describe(
                'Per-unit price as a decimal string in the merchant account currency. For example, `"19.99"`.'
              )
              .min(1)
              .max(50),
            subscriptionInfo: z
              .object({
                subscriptionSettings: z
                  .object({
                    frequency: z.enum([
                      'UNDEFINED',
                      'DAY',
                      'WEEK',
                      'MONTH',
                      'YEAR',
                    ]),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        "Number of frequency units between charges. For `DAY` frequency, the minimum is 7. The shortest\nbilling cadence supported is weekly; values 1–6 with `frequency: DAY` are rejected with\n`INVALID_ARGUMENT` (rule code `MIN_VALUE`). Default: `1`.\n\nNote that `freeTrialPeriod.interval` doesn't share this floor: a `DAY` trial may have\n`interval` as low as 1."
                      )
                      .min(1)
                      .max(3650)
                      .optional()
                      .nullable(),
                    autoRenewal: z
                      .boolean()
                      .describe(
                        "Whether the subscription renews automatically. Can't be `true` when `billingCycles` is set.\n\nDefault: `true` when both `autoRenewal` and `billingCycles` are omitted; `false` when `billingCycles`\nis set and `autoRenewal` is omitted."
                      )
                      .optional()
                      .nullable(),
                    billingCycles: z
                      .number()
                      .int()
                      .describe(
                        "Number of billing cycles before the subscription stops. Can't be set when `autoRenewal` is `true`.\n\nWhen omitted along with `autoRenewal`, the subscription renews indefinitely."
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    freeTrialPeriod: z
                      .object({
                        frequency: z.enum([
                          'UNDEFINED',
                          'DAY',
                          'WEEK',
                          'MONTH',
                          'YEAR',
                        ]),
                        interval: z
                          .number()
                          .int()
                          .describe(
                            "Length of the trial period in `frequency` units. For example, `interval: 14` with `frequency: DAY`\ngives a 14-day trial.\n\nUnlike `SubscriptionSettings.interval`, this field's `DAY` floor is `1`. No weekly floor applies\nto the trial length. Values ≤ 0 are rejected with `INVALID_ARGUMENT` (rule code\n`MIN_VALUE`)."
                          )
                          .min(1)
                          .max(999)
                          .optional(),
                      })
                      .describe('Free trial period before the first charge.')
                      .optional(),
                  })
                  .describe(
                    'Billing frequency, cycles, and free trial configuration. Required when `subscriptionInfo` is set on a\nline item.'
                  ),
                title: z
                  .string()
                  .describe(
                    'Subscription name shown to the buyer in the recurring-billing summary block on the Wix-hosted\ncheckout page (the heading of that block).'
                  )
                  .max(150)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe(
                    'Subscription description shown to the buyer in the recurring-billing summary block on the\nWix-hosted checkout page (the body text below the title).'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Subscription configuration. When set, the line item follows a scheduled-billing model defined by\n`subscriptionSettings` instead of a single immediate charge. The recurring charge each billing\ncycle is `price × quantity`, and `quantity` is locked at session creation.\n\nAt most 1 line item per request may set `subscriptionInfo`. Wix rejects requests with 2 or more\nsubscription line items with `MULTIPLE_SUBSCRIPTION_ITEMS`.'
              )
              .optional(),
            physicalProperties: z
              .object({
                shippable: z
                  .boolean()
                  .describe('Whether the item requires shipping.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Optional. Physical properties of the item, used when the item requires shipping.'
              )
              .optional(),
          })
        )
        .max(100),
      customerInfo: z
        .object({
          firstName: z
            .string()
            .describe("Buyer's first name.")
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Buyer's last name.")
            .max(100)
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe("Buyer's phone number.")
            .optional()
            .nullable(),
          email: z
            .string()
            .describe("Buyer's email address.")
            .email()
            .optional()
            .nullable(),
        })
        .describe(
          'Buyer details used to pre-fill the checkout form. The buyer can edit any of these fields on the\nWix checkout page.'
        )
        .optional(),
    })
    .describe(
      'Items, quantities, and prices the buyer is paying for, plus optional pre-fill customer details.'
    ),
  options: z.object({
    callbackUrls: z
      .object({
        postFlowUrl: z
          .string()
          .describe(
            'URL the buyer is redirected to from the Wix checkout page when they choose to leave without\ncompleting payment, either when they click **Continue browsing** or when they navigate back to\na cart-style page.\n\nDeprecated. Use `continueBrowsingUrl` instead.'
          )
          .url()
          .optional()
          .nullable(),
        thankYouPageUrl: z
          .string()
          .describe(
            'URL the buyer is redirected to after a successful payment.'
          )
          .url(),
        continueBrowsingUrl: z
          .string()
          .describe(
            'URL the buyer is redirected to from the Wix checkout page when they choose to leave without\ncompleting payment, either when they click **Continue browsing** or when they navigate back to\na cart-style page.'
          )
          .url()
          .optional()
          .nullable(),
      })
      .describe(
        'URLs the buyer is redirected to after completing or leaving the checkout flow.'
      ),
  }),
});
export const GenerateCheckoutSessionResponse = z.object({
  checkoutSession: z
    .object({
      _id: z
        .string()
        .describe('Checkout session ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      redirectUrl: z
        .string()
        .describe(
          "URL of the Wix-hosted checkout page where the buyer completes payment. The buyer enters the page\nas an anonymous shopper, and any session held by the calling app isn't carried over.\n\nThe URL carries query parameters Wix needs to render the page correctly (for example, a\n`successUrl` parameter derived from `callbackUrls.thankYouPageUrl`). Pass the URL through to the\nbuyer unchanged. Don't strip, reorder, rewrite, or otherwise mutate its query string."
        )
        .url()
        .optional()
        .nullable(),
    })
    .describe('The created checkout session.')
    .optional(),
});
